import {
  type ChangeEvent,
  type FC,
} from 'react';

import {
  useToast,
} from '@/components/base';

import {
  useDocumentBranding,
} from '@/hooks/useDocumentBranding';

const MAX_LOGO_BYTES =
  300 * 1024;

const NumberField: FC<{
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
}> = ({
  label,
  value,
  min,
  max,
  onChange,
}) => (
  <label className="space-y-1.5">
    <span className="block text-xs font-medium text-foreground-600">
      {label}
    </span>

    <input
      type="number"
      min={min}
      max={max}
      step="1"
      value={value}
      onChange={(event) => {
        const next =
          Number(
            event.target.value
          );

        if (
          Number.isFinite(next)
        ) {
          onChange(next);
        }
      }}
      className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
    />
  </label>
);

const Toggle: FC<{
  label: string;
  checked: boolean;
  onChange:
    (checked: boolean) => void;
}> = ({
  label,
  checked,
  onChange,
}) => (
  <label className="flex items-center gap-2 text-sm text-foreground-700 cursor-pointer">
    <input
      type="checkbox"
      checked={checked}
      onChange={(event) =>
        onChange(
          event.target.checked
        )
      }
      className="h-4 w-4"
    />

    <span>
      {label}
    </span>
  </label>
);

const DocumentBrandingSettingsCard:
  FC = () => {
  const {
    addToast,
  } = useToast();

  const {
    documentBranding,
    setDocumentBranding,

    isLoadingDocumentBranding,
    isSavingDocumentBranding,

    loadDocumentBranding,
    saveDocumentBranding,
  } =
    useDocumentBranding();

  const handleLogoChange =
    (
      event:
        ChangeEvent<HTMLInputElement>
    ) => {
      const file =
        event.target.files?.[0];

      event.target.value = '';

      if (!file) {
        return;
      }

      if (
        ![
          'image/png',
          'image/jpeg',
        ].includes(
          file.type
        )
      ) {
        addToast(
          'A logomarca deve ser PNG ou JPEG.',
          'warning'
        );

        return;
      }

      if (
        file.size >
        MAX_LOGO_BYTES
      ) {
        addToast(
          'A logomarca deve ter no máximo 300 KB.',
          'warning'
        );

        return;
      }

      const reader =
        new FileReader();

      reader.onload = () => {
        const value =
          typeof reader.result ===
          'string'
            ? reader.result
            : '';

        if (!value) {
          addToast(
            'Não foi possível ler a logomarca.',
            'error'
          );

          return;
        }

        setDocumentBranding(
          (previous) => ({
            ...previous,
            logoDataUrl: value,
          })
        );
      };

      reader.onerror = () => {
        addToast(
          'Não foi possível ler a logomarca.',
          'error'
        );
      };

      reader.readAsDataURL(
        file
      );
    };

  const handleSave =
    async () => {
      try {
        await saveDocumentBranding(
          documentBranding
        );

        addToast(
          'Identidade documental salva com sucesso.',
          'success'
        );
      } catch (error) {
        addToast(
          error instanceof Error
            ? error.message
            : 'Não foi possível salvar a identidade documental.',
          'error'
        );
      }
    };

  return (
    <section className="rounded-2xl border border-background-200 bg-background-50 p-4">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div>
          <h3 className="text-base font-semibold text-foreground-800">
            Identidade documental
          </h3>

          <p className="text-sm text-foreground-500 mt-0.5">
            Cabeçalho, rodapé, logomarca, paginação e margens utilizados nas exportações DOCX e PDF.
          </p>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() =>
              void loadDocumentBranding()
            }
            disabled={
              isLoadingDocumentBranding ||
              isSavingDocumentBranding
            }
            className="px-3 py-2 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 disabled:opacity-60"
          >
            Recarregar
          </button>

          <button
            type="button"
            onClick={() =>
              void handleSave()
            }
            disabled={
              isLoadingDocumentBranding ||
              isSavingDocumentBranding
            }
            className="px-3 py-2 rounded-lg text-xs font-medium bg-primary-500 text-background-50 hover:bg-primary-600 disabled:opacity-60"
          >
            {isSavingDocumentBranding
              ? 'Salvando...'
              : 'Salvar identidade'}
          </button>
        </div>
      </div>

      {isLoadingDocumentBranding ? (
        <div className="text-sm text-foreground-500 py-4">
          Carregando identidade documental...
        </div>
      ) : (
        <div className="space-y-5">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="space-y-3">
              <span className="block text-xs font-medium text-foreground-600">
                Logomarca
              </span>

              {documentBranding.logoDataUrl ? (
                <div className="rounded-xl border border-background-200 bg-white p-4 flex items-center justify-center min-h-32">
                  <img
                    src={
                      documentBranding.logoDataUrl
                    }
                    alt="Logomarca do escritório"
                    className="max-h-24 max-w-full object-contain"
                  />
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-background-300 p-6 text-center text-sm text-foreground-500">
                  Nenhuma logomarca configurada.
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <label className="px-3 py-2 rounded-lg text-xs font-medium border border-background-200 cursor-pointer hover:bg-background-100">
                  Escolher PNG/JPEG

                  <input
                    type="file"
                    accept="image/png,image/jpeg"
                    onChange={
                      handleLogoChange
                    }
                    className="hidden"
                  />
                </label>

                {documentBranding.logoDataUrl && (
                  <button
                    type="button"
                    onClick={() =>
                      setDocumentBranding(
                        (previous) => ({
                          ...previous,
                          logoDataUrl: '',
                        })
                      )
                    }
                    className="px-3 py-2 rounded-lg text-xs font-medium border border-red-200 text-red-600 hover:bg-red-50"
                  >
                    Remover logo
                  </button>
                )}
              </div>

              <p className="text-[11px] text-foreground-500">
                PNG ou JPEG, no máximo 300 KB. Este asset institucional não representa armazenamento de documentos jurídicos.
              </p>
            </div>

            <div className="space-y-3">
              <label className="space-y-1.5 block">
                <span className="block text-xs font-medium text-foreground-600">
                  Texto personalizado do cabeçalho
                </span>

                <textarea
                  rows={4}
                  value={
                    documentBranding.headerText
                  }
                  onChange={(event) =>
                    setDocumentBranding(
                      (previous) => ({
                        ...previous,
                        headerText:
                          event.target.value,
                      })
                    )
                  }
                  placeholder="Se vazio, utilizaremos automaticamente os dados institucionais do escritório."
                  className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 resize-none focus:outline-none focus:border-primary-300"
                />
              </label>

              <label className="space-y-1.5 block">
                <span className="block text-xs font-medium text-foreground-600">
                  Texto personalizado do rodapé
                </span>

                <textarea
                  rows={4}
                  value={
                    documentBranding.footerText
                  }
                  onChange={(event) =>
                    setDocumentBranding(
                      (previous) => ({
                        ...previous,
                        footerText:
                          event.target.value,
                      })
                    )
                  }
                  placeholder="Se vazio, utilizaremos o rodapé padrão e os dados já cadastrados do escritório."
                  className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 resize-none focus:outline-none focus:border-primary-300"
                />
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 rounded-xl border border-background-200 p-4">
            <Toggle
              label="Mostrar logomarca"
              checked={
                documentBranding.showLogo
              }
              onChange={(value) =>
                setDocumentBranding(
                  (previous) => ({
                    ...previous,
                    showLogo: value,
                  })
                )
              }
            />

            <Toggle
              label="Mostrar cabeçalho"
              checked={
                documentBranding.showHeader
              }
              onChange={(value) =>
                setDocumentBranding(
                  (previous) => ({
                    ...previous,
                    showHeader: value,
                  })
                )
              }
            />

            <Toggle
              label="Mostrar rodapé"
              checked={
                documentBranding.showFooter
              }
              onChange={(value) =>
                setDocumentBranding(
                  (previous) => ({
                    ...previous,
                    showFooter: value,
                  })
                )
              }
            />

            <Toggle
              label="Numerar páginas"
              checked={
                documentBranding.showPageNumbers
              }
              onChange={(value) =>
                setDocumentBranding(
                  (previous) => ({
                    ...previous,
                    showPageNumbers:
                      value,
                  })
                )
              }
            />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-foreground-700 mb-3">
              Layout da página
            </h4>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <NumberField
                label="Logo (mm)"
                value={
                  documentBranding.logoWidthMm
                }
                min={10}
                max={60}
                onChange={(value) =>
                  setDocumentBranding(
                    (previous) => ({
                      ...previous,
                      logoWidthMm: value,
                    })
                  )
                }
              />

              <NumberField
                label="Superior (mm)"
                value={
                  documentBranding.marginTopMm
                }
                min={10}
                max={50}
                onChange={(value) =>
                  setDocumentBranding(
                    (previous) => ({
                      ...previous,
                      marginTopMm: value,
                    })
                  )
                }
              />

              <NumberField
                label="Direita (mm)"
                value={
                  documentBranding.marginRightMm
                }
                min={10}
                max={50}
                onChange={(value) =>
                  setDocumentBranding(
                    (previous) => ({
                      ...previous,
                      marginRightMm: value,
                    })
                  )
                }
              />

              <NumberField
                label="Inferior (mm)"
                value={
                  documentBranding.marginBottomMm
                }
                min={10}
                max={50}
                onChange={(value) =>
                  setDocumentBranding(
                    (previous) => ({
                      ...previous,
                      marginBottomMm: value,
                    })
                  )
                }
              />

              <NumberField
                label="Esquerda (mm)"
                value={
                  documentBranding.marginLeftMm
                }
                min={10}
                max={50}
                onChange={(value) =>
                  setDocumentBranding(
                    (previous) => ({
                      ...previous,
                      marginLeftMm: value,
                    })
                  )
                }
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default
  DocumentBrandingSettingsCard;